import React, { Suspense, useState, useEffect, useRef } from 'react';
import { MessageSquare, Calendar, Settings, X, WifiOff, AlertTriangle, BookUser, ClipboardList, FileText } from 'lucide-react';
import { ModuleType, User, UserStatus } from './types';
// Removed mock services/api usage; using backend HTTP endpoints
import { LoginPage } from './components/LoginPage';
import { BootstrapAdminPage } from './components/BootstrapAdminPage';
import { DesktopServerSetupPage } from './components/DesktopServerSetupPage';
import { ToastProvider, useToast } from './components/ui/Toaster';
import { TopbarSearchButton } from './components/topbar/TopbarSearchButton';
import { ProfileMenu } from './components/topbar/ProfileMenu';
import { addDays, isSameDay, startOfDay } from 'date-fns';
import { connectSocket, disconnectSocket, getSocket, onInvalidate } from './services/socket';
import { HttpError, http } from './services/http';
import { toUserError } from './services/errors';
import { useTheme } from './components/hooks/useTheme';
import { useSettingsPoll } from './components/hooks/useSettingsPoll';
import { useNotifications as useNotificationsHook } from './components/hooks/useNotifications';
import { syncNotificationSoundSettings } from './services/sound';

const MessengerModule = React.lazy(() => import('./components/MessengerModule').then((m) => ({ default: m.MessengerModule })));
const CalendarModule = React.lazy(() => import('./components/CalendarModule').then((m) => ({ default: m.CalendarModule })));
const AbsencesModule = React.lazy(() => import('./components/absences/AbsencesModule').then((m) => ({ default: m.AbsencesModule })));
const JournalModule = React.lazy(() => import('./components/journal/JournalModule').then((m) => ({ default: m.JournalModule })));
const AddressBookModule = React.lazy(() => import('./components/addressbook/AddressBookModule').then((m) => ({ default: m.AddressBookModule })));
const AdminModule = React.lazy(() => import('./components/AdminModule').then((m) => ({ default: m.AdminModule })));
const RequestsModule = React.lazy(() => import('./components/requests/RequestsModule').then((m) => ({ default: m.RequestsModule })));
const ProfileSettingsModal = React.lazy(() => import('./components/ProfileSettingsModal').then((m) => ({ default: m.ProfileSettingsModal })));
const ProfileDevicesModal = React.lazy(() => import('./components/ProfileDevicesModal').then((m) => ({ default: m.ProfileDevicesModal })));
const GlobalSearchModal = React.lazy(() => import('./components/GlobalSearchModal').then((m) => ({ default: m.GlobalSearchModal })));
const QrConnectPage = React.lazy(() => import('./components/QrConnectPage').then((m) => ({ default: m.QrConnectPage })));

const ModuleFallback: React.FC = () => (
  <div className="flex h-full min-h-[240px] items-center justify-center rounded-2xl border border-gray-200 bg-white/80 text-sm text-gray-500 shadow-sm dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-400">
    Загрузка интерфейса...
  </div>
);

const AppContent: React.FC = () => {
  const isDesktop = typeof window !== 'undefined' && !!window.desktopApi;
  const isQrConnectPage = typeof window !== 'undefined' && /^\/(qr-connect|android-download)\/?$/.test(window.location.pathname || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeModule, setActiveModule] = useState<ModuleType>(ModuleType.MESSENGER);
  const [currentUser, setCurrentUser] = useState<User>({ id: 'me', name: 'Пользователь', username: '', email: undefined, role: 'user', status: UserStatus.OFFLINE });
  const [bootstrapChecked, setBootstrapChecked] = useState(false);
  const [bootstrapHasUsers, setBootstrapHasUsers] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showProfileDevices, setShowProfileDevices] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [todayEventCount, setTodayEventCount] = useState(0);
  const [absencesApprovalsCount, setAbsencesApprovalsCount] = useState(0);
  const [requestsResponsibleActiveCount, setRequestsResponsibleActiveCount] = useState(0);
  const [serverReadyChecked, setServerReadyChecked] = useState(() => !isDesktop);
  const [serverReady, setServerReady] = useState(() => !isDesktop);
  const [serverSetupError, setServerSetupError] = useState<string | null>(null);
  const { addToast } = useToast();
  const autoUpdateCheckedRef = useRef(false);

  const hasPerm = (p: string) => {
    try { return Array.isArray(permissions) && permissions.includes(p); } catch { return false; }
  };

  const openProfileSettings = () => {
    try { window.dispatchEvent(new Event('al_profile_settings_open')); } catch {}
    setShowProfileSettings(true);
  };

  const closeProfileSettings = () => {
    try { window.dispatchEvent(new Event('al_profile_settings_close')); } catch {}
    setShowProfileSettings(false);
  };

  const openProfileDevices = () => {
    setShowProfileDevices(true);
  };

  const closeProfileDevices = () => {
    setShowProfileDevices(false);
  };

  // Navigation props for Deep Linking
  const [messengerProps, setMessengerProps] = useState<{initialChatId?: string | null, initialMessageId?: string | null}>({});
  const [calendarProps, setCalendarProps] = useState<{initialDate?: Date | null, initialEventId?: string | null}>({});
  const [addressbookProps, setAddressbookProps] = useState<{ initialContactId?: string | null }>({});
  const [requestsProps, setRequestsProps] = useState<{ initialRequestId?: string | null }>({});
  const [adminProps, setAdminProps] = useState<{ initialTab?: string | null }>({});

  const notifications = useNotificationsHook(isAuthenticated, currentUser.status, currentUser.id);

  const messengerUnreadCount = notifications
    .filter(n => n.type === 'chat')
    .reduce((acc, n) => acc + Number(n.unread ?? 0), 0);

  const desktopUnreadCount = notifications.reduce((acc, n) => {
    if (n.type === 'chat') return acc + Number(n.unread ?? 0);
    if (n.type === 'mention') {
      if (n.unread !== undefined && n.unread !== null) return acc + Number(n.unread);
      return acc + (n.isRead ? 0 : 1);
    }
    return acc;
  }, 0);

  const modules = [
    { id: ModuleType.MESSENGER, label: 'Чат', icon: MessageSquare, count: messengerUnreadCount, enabled: true },
    { id: ModuleType.CALENDAR, label: 'Календарь', icon: Calendar, count: todayEventCount, enabled: hasPerm('module_calendar') },
    { id: ModuleType.ABSENCES, label: 'Отсутствия', icon: Calendar, count: absencesApprovalsCount, enabled: hasPerm('module_absences') },
    { id: ModuleType.JOURNAL, label: 'Журнал', icon: FileText, enabled: hasPerm('module_journal') },
    { id: ModuleType.ADDRESSBOOK, label: 'Контакты', icon: BookUser, enabled: hasPerm('module_addressbook') },
    { id: ModuleType.REQUESTS, label: 'Заявки', icon: ClipboardList, count: requestsResponsibleActiveCount, enabled: hasPerm('module_requests') },
    { id: ModuleType.ADMIN, label: 'Администрирование', icon: Settings, enabled: hasPerm('module_admin') },
  ].filter((m) => m.enabled);

  useEffect(() => {
    const allowedIds = new Set(modules.map((m) => m.id));
    if (!allowedIds.has(activeModule)) {
      setActiveModule(ModuleType.MESSENGER);
    }
  }, [activeModule, modules]);

  useEffect(() => {
    if (!isDesktop || !isAuthenticated || autoUpdateCheckedRef.current) return;
    autoUpdateCheckedRef.current = true;
    (async () => {
      try {
        const info = await window.desktopApi?.checkForUpdates();
        if (info?.supported && info?.updateAvailable && info?.file) {
          await window.desktopApi?.downloadAndInstallUpdate(String(info.file || ''));
        }
      } catch {}
    })();
  }, [isDesktop, isAuthenticated]);

  useEffect(() => {
    try { syncNotificationSoundSettings(currentUser.preferences?.notifications as any); } catch {}
  }, [currentUser.preferences]);

  useEffect(() => {
    if (!isDesktop) return;
    try {
      void window.desktopApi?.setUnread?.(desktopUnreadCount > 0);
    } catch {}
  }, [isDesktop, desktopUnreadCount]);

  // Deep link via query params (works for links from bot messages)
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const sp = new URLSearchParams(window.location.search || '');
      const open = String(sp.get('open') || '').trim();
      if (open !== 'calendar') return;
      const eventId = String(sp.get('eventId') || '').trim() || null;
      const dateRaw = String(sp.get('date') || '').trim();
      const date = dateRaw ? new Date(dateRaw) : null;
      setCalendarProps({ initialDate: date && !Number.isNaN(date.getTime()) ? date : null, initialEventId: eventId });
      setActiveModule(ModuleType.CALENDAR);
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''));
    } catch {}
  }, []);

  // Deep link to Admin via query params: ?open=admin&tab=users
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const sp = new URLSearchParams(window.location.search || '');
      const open = String(sp.get('open') || '').trim();
      if (open !== 'admin') return;
      const tab = String(sp.get('tab') || '').trim() || null;
      setAdminProps({ initialTab: tab });
      setActiveModule(ModuleType.ADMIN);
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''));
    } catch {}
  }, []);

  const syncAdminUrl = React.useCallback((tab: string | null) => {
    try {
      void tab;
    } catch {}
  }, []);

  // --- THEME LOGIC ---
  useTheme(isAuthenticated, currentUser.preferences?.themeColor, currentUser.preferences?.theme, currentUser.preferences?.fontScale);

  // Desktop: validate server URL by checking /ready before any auth/bootstrap calls
  useEffect(() => {
    if (!isDesktop || isQrConnectPage) return;
    let cancelled = false;

    (async () => {
      try {
        const r = await http.get<{ ready: boolean }>('/ready');
        const ok = !!(r as any)?.ready;
        if (cancelled) return;
        setServerReady(ok);
        setServerSetupError(ok ? null : 'Сервер ещё не готов');
      } catch (err: any) {
        let fallback = 'Не удалось подключиться к серверу';
        if (err instanceof HttpError) {
          const body = (err as any).body;
          if (body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'ready') && body.ready === false) {
            fallback = 'Сервер ещё не готов';
          }
        }
        const ue = toUserError(err, fallback);
        if (cancelled) return;
        setServerReady(false);
        setServerSetupError(ue.message);
      } finally {
        if (cancelled) return;
        setServerReadyChecked(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isDesktop, isQrConnectPage]);

  // Bootstrap auth by verifying /me (do not trust stale localStorage token)
  useEffect(() => {
    if (!serverReady || isQrConnectPage) return;
    (async () => {
      try {
        const me = await http.get<any>('/me');
        if (me && me.id) {
          setCurrentUser(prev => ({ ...prev, ...me }));
          try { localStorage.setItem('al_user', JSON.stringify(me)); } catch {}
          setIsAuthenticated(true);
          return;
        }
      } catch {
        try { localStorage.removeItem('al_jwt'); } catch {}
        try { localStorage.removeItem('al_user'); } catch {}
        try { localStorage.removeItem('al_must_change_password'); } catch {}
        setIsAuthenticated(false);
      }
    })();
  }, [serverReady, isQrConnectPage]);

  // If not authenticated, check whether we need first-admin bootstrap
  useEffect(() => {
    if (!serverReady || isQrConnectPage) return;
    if (isAuthenticated) return;
    (async () => {
      try {
        const s = await http.get<{ hasUsers: boolean }>('/bootstrap/status');
        setBootstrapHasUsers(!!s?.hasUsers);
      } catch {
        // Fail safe: show login page
        setBootstrapHasUsers(true);
      } finally {
        setBootstrapChecked(true);
      }
    })();
  }, [isAuthenticated, serverReady, isQrConnectPage]);

  // Online Status & Settings Poll
  useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
      };
  }, []);

  const { maintenanceMode: spMaintenanceMode, organizationName, maxFileSizeMb } = useSettingsPoll(isAuthenticated);
  const resolvedOrgName = String(organizationName || 'АрхивЛинк');
  const orgInitial = (resolvedOrgName.trim()[0] || 'А').toUpperCase();
  useEffect(() => {
    setMaintenanceMode(!!spMaintenanceMode);
  }, [spMaintenanceMode]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!isAuthenticated) {
      document.title = resolvedOrgName;
      return;
    }
    if (activeModule !== ModuleType.MESSENGER) {
      document.title = resolvedOrgName;
    }
  }, [isAuthenticated, resolvedOrgName, activeModule]);

  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      const raw = localStorage.getItem('al_user');
      if (raw) {
        const stored = JSON.parse(raw);
        setCurrentUser(prev => ({ ...prev, ...stored }));
      }
    } catch {}
  }, [isAuthenticated]);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) return;
      try {
        const me = await http.get<any>('/me');
        if (me && me.id) setCurrentUser(prev => ({ ...prev, ...me }));
      } catch {}
    })();
  }, [isAuthenticated]);

  // Enforce first-login password change
  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      const must = localStorage.getItem('al_must_change_password') === '1';
      if (must) {
        setMustChangePassword(true);
        openProfileSettings();
      }
    } catch {}
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) { disconnectSocket(); return; }
    const s = connectSocket();
    const onConnect = () => {
      setIsOnline(true);
      setCurrentUser(prev => ({ ...prev, status: UserStatus.ONLINE }));
    };
    const onDisconnect = () => {
      setIsOnline(navigator.onLine);
      setCurrentUser(prev => ({ ...prev, status: UserStatus.OFFLINE }));
    };
    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
        http.get<string[]>('/permissions').then(setPermissions).catch(() => setPermissions([]));
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }
  }, [isAuthenticated, currentUser.role]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const off = onInvalidate((e) => {
      if (e.scope !== 'permissions') return;
      http.get<string[]>('/permissions').then(setPermissions).catch(() => setPermissions([]));
    }, ['permissions']);
    return () => {
      try { off && off(); } catch {}
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAbsencesApprovalsCount(0);
      return;
    }
    if (!permissions.includes('timeoff_approve')) {
      setAbsencesApprovalsCount(0);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const r = await http.get<{ count: number }>('/absences/approvals/count');
        if (!cancelled) setAbsencesApprovalsCount(Number(r?.count || 0));
      } catch {
        if (!cancelled) setAbsencesApprovalsCount(0);
      }
    };

    load();
    const off = onInvalidate((e) => {
      if (e.scope !== 'absences') return;
      load();
    }, ['absences']);

    const tickInterval = () => {
      const s = getSocket();
      if (s && s.connected) return;
      load();
    };

    const t = setInterval(tickInterval, 300_000);
    return () => {
      cancelled = true;
      clearInterval(t);
      try { off && off(); } catch {}
    };
  }, [isAuthenticated, permissions]);

  useEffect(() => {
    if (!isAuthenticated) {
      setRequestsResponsibleActiveCount(0);
      return;
    }
    if (!permissions.includes('module_requests')) {
      setRequestsResponsibleActiveCount(0);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const r = await http.get<{ activeCount: number }>('/requests/responsible/active-count');
        if (!cancelled) setRequestsResponsibleActiveCount(Number((r as any)?.activeCount || 0));
      } catch {
        if (!cancelled) setRequestsResponsibleActiveCount(0);
      }
    };

    load();
    const off = onInvalidate((e) => {
      if (e.scope !== 'requests') return;
      load();
    }, ['requests' as any]);

    const t = setInterval(load, 300_000);
    return () => {
      cancelled = true;
      clearInterval(t);
      try { off && off(); } catch {}
    };
  }, [isAuthenticated, permissions]);

  // Calendar Reminders Logic & Today's Count
  useEffect(() => {
    if (!isAuthenticated) return;

    let stopped = false;
    let timer: any = null;

    const nextDelayMs = () => {
      const visible = typeof document === 'undefined' ? true : document.visibilityState === 'visible';
      const s = getSocket();
      const connected = !!(s && s.connected);
      const base = connected
        ? (visible ? 900000 : 1800000)
        : (visible ? 120000 : 300000);
      const jitter = Math.floor(Math.random() * 3000);
      return base + jitter;
    };

    const checkReminders = async () => {
      try {
        const now = new Date();
        const from = startOfDay(now);
        const to = startOfDay(addDays(now, 1));
        const qs = `?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
        const events = (await http.get<any[]>(`/calendar/events${qs}`).catch(() => [] as any[]))
          .map(e => ({ ...e, start: new Date(e.start), end: new Date(e.end) }));
        const todayCount = events.filter(e => isSameDay(e.start, now)).length;
        setTodayEventCount(todayCount);
      } catch (e) {
        console.error(e);
      }
    };

    const schedule = async () => {
      if (stopped) return;
      await checkReminders();
      if (stopped) return;
      timer = setTimeout(schedule, nextDelayMs());
    };

    schedule();

    const off = onInvalidate((e) => {
      if (e.scope === 'calendar') {
        checkReminders();
      }
    }, ['calendar']);

    const onVis = () => {
      if (stopped) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(schedule, nextDelayMs());
    };
    try {
      document.addEventListener('visibilitychange', onVis);
    } catch {}

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      try {
        document.removeEventListener('visibilitychange', onVis);
      } catch {}
      try { off && off(); } catch {}
    };
  }, [isAuthenticated]);

  const refreshUser = async () => {
      try {
          const user = await http.get<any>('/me');
          setCurrentUser(prev => ({ ...prev, ...user }));
          try { localStorage.setItem('al_user', JSON.stringify(user)); } catch {}
      } catch (e) {
          console.error("Failed to refresh user", e);
      }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const off = onInvalidate((e) => {
      if (e.scope !== 'settings' && e.scope !== 'users' && e.scope !== 'notifications') return;
      void refreshUser();
    }, ['settings', 'users', 'notifications']);
    return () => {
      try { off && off(); } catch {}
    };
  }, [isAuthenticated]);

  

  const handleGlobalNavigate = React.useCallback((type: 'chat' | 'event' | 'contact' | 'request' | 'admin', id: string, subId?: string) => {
      if (type === 'chat') {
          setMessengerProps({ initialChatId: id, initialMessageId: subId });
          setActiveModule(ModuleType.MESSENGER);
      } else if (type === 'event') {
          // subId is iso string date
          setCalendarProps({ initialDate: subId ? new Date(subId) : null, initialEventId: id });
          setActiveModule(ModuleType.CALENDAR);
      } else if (type === 'contact') {
          setAddressbookProps({ initialContactId: id });
          setActiveModule(ModuleType.ADDRESSBOOK);
      } else if (type === 'request') {
          setRequestsProps({ initialRequestId: id });
          setActiveModule(ModuleType.REQUESTS);
      } else if (type === 'admin') {
          const tab = (subId && String(subId).trim()) ? String(subId).trim() : null;
          setAdminProps({ initialTab: tab });
          setActiveModule(ModuleType.ADMIN);
          syncAdminUrl(tab);
      }
      setShowGlobalSearch(false);
  }, [syncAdminUrl]);

  useEffect(() => {
    if (activeModule !== ModuleType.CALENDAR) return;
    if (!calendarProps.initialDate && !calendarProps.initialEventId) return;
    const t = window.setTimeout(() => {
      setCalendarProps({});
    }, 0);
    return () => {
      try { window.clearTimeout(t); } catch {}
    };
  }, [activeModule, calendarProps.initialDate, calendarProps.initialEventId]);

  useEffect(() => {
    const onNavigate = (e: any) => {
      try {
        const d = (e as CustomEvent).detail as any;
        const type = d?.type as any;
        const id = d?.id as any;
        const subId = d?.subId as any;
        if (type === 'admin') {
          handleGlobalNavigate('admin', 'admin', typeof subId === 'string' ? subId : undefined);
          return;
        }
        if ((type === 'chat' || type === 'event' || type === 'contact' || type === 'request') && typeof id === 'string' && id) {
          handleGlobalNavigate(type, id, typeof subId === 'string' ? subId : undefined);
        }
      } catch {}
    };
    try {
      window.addEventListener('al_global_navigate', onNavigate as any);
    } catch {}
    return () => {
      try {
        window.removeEventListener('al_global_navigate', onNavigate as any);
      } catch {}
    };
  }, [handleGlobalNavigate]);

  const handleLogout = async () => {
      try { await http.post('/auth/logout', {}); } catch {}
      try { localStorage.removeItem('al_jwt'); } catch {}
      try { localStorage.removeItem('al_user'); } catch {}
      try { localStorage.removeItem('al_must_change_password'); } catch {}
      disconnectSocket();
      setCurrentUser({ id: 'me', name: 'Пользователь', username: '', email: undefined, role: 'user', status: UserStatus.OFFLINE });
      setIsAuthenticated(false);
      setBootstrapChecked(false);
      setProfileMenuOpen(false);
      addToast('Вы вышли из системы', 'success');
  };

  if (isDesktop && !serverReadyChecked) {
      return <div className="min-h-screen bg-gray-50" />;
  }

  if (isQrConnectPage) {
      return (
        <Suspense fallback={<ModuleFallback />}>
          <QrConnectPage />
        </Suspense>
      );
  }

  if (isDesktop && !serverReady) {
      return <DesktopServerSetupPage organizationName={resolvedOrgName} initialError={serverSetupError} />;
  }

  if (!isAuthenticated) {
      if (!bootstrapChecked) {
        return <div className="min-h-screen bg-gray-50" />;
      }
      if (!bootstrapHasUsers) {
        return (
          <BootstrapAdminPage
            organizationName={resolvedOrgName}
            onBootstrapped={() => setIsAuthenticated(true)}
            onGoToLogin={() => { setBootstrapHasUsers(true); }}
          />
        );
      }
      return <LoginPage onLogin={() => setIsAuthenticated(true)} organizationName={resolvedOrgName} />;
  }

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden bg-gray-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 transition-colors duration-300">
      {/* Maintenance Banner */}
      {maintenanceMode && (
            <div className="bg-orange-500 text-white text-xs font-bold py-1 px-4 text-center z-40 flex items-center justify-center gap-2 shadow-md animate-slide-down">
                <AlertTriangle className="w-3 h-3" />
                Внимание! Ведутся технические работы. Возможны перебои в работе системы.
            </div>
      )}

      {/* Top Panel */}
      <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-sm z-30 flex items-center justify-between px-4 shrink-0 relative transition-colors min-h-16 gap-2 flex-nowrap">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-sm transition-colors">
              {orgInitial}
            </div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100 tracking-tight truncate max-w-[42vw]">{resolvedOrgName}</h1>
          </div>
          
          <nav className="flex items-center gap-1 ml-0 sm:ml-4 min-w-0 max-w-full overflow-x-auto whitespace-nowrap">
            {modules.map((mod) => (
              <button
                key={mod.id}
                type="button"
                onClick={() => {
                  setActiveModule(mod.id);
                  if (mod.id === ModuleType.ADMIN) {
                    syncAdminUrl(adminProps.initialTab || null);
                    return;
                  }
                  try {
                    if (typeof window === 'undefined') return;
                    const url = new URL(window.location.href);
                    const sp = url.searchParams;
                    if (String(sp.get('open') || '').trim() === 'admin') {
                      sp.delete('open');
                      sp.delete('tab');
                      const next = sp.toString() ? `${url.pathname}?${sp.toString()}${url.hash || ''}` : `${url.pathname}${url.hash || ''}`;
                      window.history.replaceState({}, '', next);
                    }
                  } catch {}
                }}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeModule === mod.id
                    ? 'bg-brand-50 text-brand-600'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                }`}
                aria-current={activeModule === mod.id ? 'page' : undefined}
              >
                <mod.icon className="w-4 h-4" />
                {mod.label}
                {mod.count !== undefined && mod.count > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px]">
                    {mod.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <TopbarSearchButton onClick={() => setShowGlobalSearch(true)} />
          <ProfileMenu 
            currentUser={currentUser}
            open={profileMenuOpen}
            onOpenChange={setProfileMenuOpen}
            onOpenSettings={() => { setProfileMenuOpen(false); openProfileSettings(); }}
            onOpenDevices={() => { setProfileMenuOpen(false); openProfileDevices(); }}
            onLogout={() => { void handleLogout(); }}
          />
        </div>
      </header>

      {/* Mobile Menu Overlay */}

      {/* Main Content Area */}
      <main className="flex-1 overflow-hidden relative">
        <div className="absolute inset-0 p-4">
          <Suspense fallback={<ModuleFallback />}>
            {activeModule === ModuleType.MESSENGER && <MessengerModule initialChatId={messengerProps.initialChatId} initialMessageId={messengerProps.initialMessageId} permissions={permissions} currentUser={currentUser} maxFileSizeMb={maxFileSizeMb} />}
            {activeModule === ModuleType.CALENDAR && <CalendarModule initialDate={calendarProps.initialDate} initialEventId={calendarProps.initialEventId} currentUser={currentUser} permissions={permissions} />}
            {activeModule === ModuleType.ABSENCES && <AbsencesModule permissions={permissions} currentUser={currentUser} />}
            {activeModule === ModuleType.JOURNAL && <JournalModule permissions={permissions} currentUser={currentUser} />}
            {activeModule === ModuleType.ADDRESSBOOK && <AddressBookModule initialContactId={addressbookProps.initialContactId} permissions={permissions} currentUser={currentUser} />}
            {activeModule === ModuleType.REQUESTS && (
              <RequestsModule
                permissions={permissions}
                currentUserId={currentUser.id}
                initialRequestId={requestsProps.initialRequestId}
                onInitialRequestConsumed={() => setRequestsProps({ initialRequestId: null })}
              />
            )}
            {activeModule === ModuleType.ADMIN && (
              <AdminModule
                permissions={permissions}
                currentUser={currentUser}
                initialTab={adminProps.initialTab as any}
                onTabChange={(tab) => setAdminProps({ initialTab: tab })}
              />
            )}
          </Suspense>
        </div>
      </main>

      {/* Offline Status */}
      {!isOnline && (
        <div className="fixed bottom-0 left-0 w-full bg-red-600 text-white text-xs font-bold py-1 px-4 text-center z-50 flex items-center justify-center gap-2">
            <WifiOff className="w-3 h-3" />
            Отсутствует подключение к интернету
        </div>
      )}

      {showProfileSettings && (
          <Suspense fallback={null}>
            <ProfileSettingsModal 
              user={currentUser} 
              initialTab={mustChangePassword ? 'security' : undefined}
              onClose={closeProfileSettings} 
              onUserUpdate={refreshUser}
            />
          </Suspense>
      )}

      {showProfileDevices && (
          <Suspense fallback={null}>
            <ProfileDevicesModal onClose={closeProfileDevices} />
          </Suspense>
      )}
      
      {showGlobalSearch && (
          <Suspense fallback={null}>
            <GlobalSearchModal 
              onClose={() => setShowGlobalSearch(false)}
              onNavigate={handleGlobalNavigate}
            />
          </Suspense>
      )}

    </div>
  );
};

const App: React.FC = () => {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
};

export default App;
